// CRNA room MOVE / SWAP / REMOVE rules for the per-day nurse-transfer overlay, as pure functions over the
// overlay map. Extracted because the same handlers were copy-pasted into BoardView.tsx and CrnaView.tsx and
// drifted apart — the swap guard existed in one path and not the other, which is how a CRNA ended up in two
// operating rooms at once. Same precedent as roomTransfers.ts / floatCrnas.ts / boardViewHelpers.ts.
//
// THE INVARIANT these functions exist to hold: **no CRNA is ever the effective nurse of two rooms.** A CRNA
// staffs exactly one OR. A room's effective nurse is the overlay target when the room has an entry, otherwise
// its raw allocated CRNA (see effectiveNurseIdsForRoom), so the overlay and the raw allocations can disagree
// in two ways that both double-book someone:
//   1. two overlay entries naming the same CRNA;
//   2. an overlay entry seating a CRNA on one room while a raw allocation still holds them on another.
// Case 2 is the one a plain `delete` reintroduces, which is why NURSE_TRANSFER_VACANT exists: reverting a room
// to its raw CRNA is only safe when that CRNA is not seated elsewhere.
import { NURSE_TRANSFER_VACANT, effectiveNurseIdsForRoom } from './nurseTransfers.ts';

export type NurseTransfers = Record<string, string>;
// operatingRoomId -> the CRNA ids RAW-allocated to that room (before the overlay is applied). Rooms with no
// CRNA may be present with an empty array; rooms absent entirely are treated as having none.
export type RawCrnaByRoom = Record<string, string[]>;

// Every room each CRNA effectively holds under this overlay. The single source of truth for the invariant —
// tests assert on it, and normalizeNurseTransfers uses it to decide when a revert is unsafe.
export function effectiveRoomsByNurse(transfers: NurseTransfers, rawCrnaByRoom: RawCrnaByRoom): Map<string, Set<string>> {
  const out = new Map<string, Set<string>>();
  const add = (nurseId: string, roomId: string) => {
    if (!out.has(nurseId)) out.set(nurseId, new Set());
    out.get(nurseId)!.add(roomId);
  };
  const roomIds = new Set([...Object.keys(rawCrnaByRoom), ...Object.keys(transfers)]);
  for (const roomId of roomIds) {
    const to = transfers[roomId];
    if (to === NURSE_TRANSFER_VACANT) continue;              // explicitly emptied
    if (to !== undefined) { add(to, roomId); continue; }     // overlay target owns the room
    for (const id of rawCrnaByRoom[roomId] || []) add(id, roomId); // otherwise the raw allocation does
  }
  return out;
}

// Repair case 2 above: any room with NO overlay entry whose raw CRNA is seated on a DIFFERENT room by the
// overlay is vacated, because letting it fall through to its raw allocation would put that person in two ORs.
// Applied at the end of every operation here, so no caller can forget it.
export function normalizeNurseTransfers(transfers: NurseTransfers, rawCrnaByRoom: RawCrnaByRoom): NurseTransfers {
  const next: NurseTransfers = { ...transfers };
  const seatedElsewhere = new Map<string, string>(); // nurseId -> the room the overlay seats them on
  for (const [roomId, to] of Object.entries(next)) {
    if (to !== NURSE_TRANSFER_VACANT) seatedElsewhere.set(to, roomId);
  }
  for (const [roomId, rawIds] of Object.entries(rawCrnaByRoom)) {
    if (next[roomId] !== undefined) continue; // room already has an explicit entry — nothing to fall through to
    if (rawIds.some(id => seatedElsewhere.has(id) && seatedElsewhere.get(id) !== roomId)) {
      next[roomId] = NURSE_TRANSFER_VACANT;
    }
  }
  return next;
}

// Take a CRNA off every room they effectively hold, except `keepRoomId`. Overlay entries naming them are
// dropped; a room where they are RAW-allocated is VACATED (dropping nothing there would leave the raw
// allocation still showing them). This is what turns a one-way "move" into a genuine move rather than a clone.
export function releaseNurse(
  transfers: NurseTransfers,
  rawCrnaByRoom: RawCrnaByRoom,
  employeeId: string,
  keepRoomId?: string,
): NurseTransfers {
  const next: NurseTransfers = { ...transfers };
  for (const roomId of Object.keys(next)) {
    if (roomId !== keepRoomId && next[roomId] === employeeId) delete next[roomId];
  }
  for (const [roomId, rawIds] of Object.entries(rawCrnaByRoom)) {
    if (roomId === keepRoomId) continue;
    if (next[roomId] === undefined && rawIds.includes(employeeId)) next[roomId] = NURSE_TRANSFER_VACANT;
  }
  return normalizeNurseTransfers(next, rawCrnaByRoom);
}

// Hand ONE room to `toNurseId`. The target is released from wherever they were FIRST, so moving onto a CRNA who
// already holds a room is a move, not a double-booking — the gap that swapNurseRoom guarded against and the
// one-way move did not.
export function moveNurseToRoom(
  transfers: NurseTransfers,
  rawCrnaByRoom: RawCrnaByRoom,
  operatingRoomId: string,
  toNurseId: string,
): NurseTransfers {
  const next = releaseNurse(transfers, rawCrnaByRoom, toNurseId, operatingRoomId);
  next[operatingRoomId] = toNurseId;
  return normalizeNurseTransfers(next, rawCrnaByRoom);
}

// The person card's "transfer all": hand this CRNA's rooms to `toNurseId`. Looping moveNurseToRoom would fight
// itself — each call releases the target from the room the previous call just gave them, so only the last room
// would stick and every earlier one would be left vacated. A CRNA is single-room by definition, so the target
// takes ONE room (the first given, which callers pass in board order) and any others keep their current
// occupant. Callers that hold several rooms are a data anomaly; this resolves it without double-booking anyone
// and without silently emptying a staffed room.
export function moveNurseToRooms(
  transfers: NurseTransfers,
  rawCrnaByRoom: RawCrnaByRoom,
  operatingRoomIds: string[],
  toNurseId: string,
): NurseTransfers {
  if (!operatingRoomIds.length) return { ...transfers };
  return moveNurseToRoom(transfers, rawCrnaByRoom, operatingRoomIds[0], toNurseId);
}

// Swap: this room goes to `withNurseId` and that CRNA's own room goes to `fromNurseId`. Both entries are
// written together (a single map), because writing them one at a time leaves a window — and a caller that
// forgets the second write double-books. When the target holds no room this degrades to a plain move, which
// is correct: there is nothing to trade back.
export function swapNurseRooms(
  transfers: NurseTransfers,
  rawCrnaByRoom: RawCrnaByRoom,
  operatingRoomId: string,
  fromNurseId: string,
  withNurseId: string,
  otherRoomId?: string,
): NurseTransfers {
  if (fromNurseId === withNurseId) return { ...transfers };
  const next: NurseTransfers = { ...transfers };
  next[operatingRoomId] = withNurseId;
  if (otherRoomId && otherRoomId !== operatingRoomId) {
    next[otherRoomId] = fromNurseId;
    return normalizeNurseTransfers(next, rawCrnaByRoom);
  }
  // Nothing to trade back (the target is a bullpen CRNA): degrade to a plain move.
  return releaseNurse(next, rawCrnaByRoom, withNurseId, operatingRoomId);
}

// Take `employeeId` off `roomId` (dragging their plate to the bullpen). The room reverts to its raw allocated
// CRNA only when that person is not already seated elsewhere by the overlay; otherwise it is VACATED. Deleting
// the key unconditionally is what made swap-then-remove put the partner's CRNA in two rooms at once:
//   raw roomA=[Y] roomB=[X];  swap -> {roomA:X, roomB:Y};  remove X by deleting roomA -> roomA reverts to Y,
//   which the overlay still seats on roomB.
export function removeNurseFromRoom(
  transfers: NurseTransfers,
  rawCrnaByRoom: RawCrnaByRoom,
  operatingRoomId: string,
  employeeId: string,
): NurseTransfers {
  const next: NurseTransfers = { ...transfers };
  // Drop every overlay entry seating this person — a removal takes them off the board, not off one room.
  for (const roomId of Object.keys(next)) if (next[roomId] === employeeId) delete next[roomId];
  // Deliberately NOT releaseNurse: the caller strips this room's RAW allocation itself, so writing a vacancy
  // here too would outlive the removal and keep the room empty through a later re-allocation (the overlay is
  // only reset by a new simulation). normalize still vacates when reverting would double-book the raw CRNA.
  return normalizeNurseTransfers(next, rawCrnaByRoom);
}

// Translate a computed overlay into per-schedule allocatedNurseIds edits, so a CRNA move/swap can PERSIST to the
// allocation (updateAllocations) instead of only living in the browser overlay — the fix for "the CRNA-tab
// waterfall reverts on save". For every schedule whose ROOM the overlay touches, the new nurse list is the CRNA
// the overlay seats there (none if VACANT) plus that schedule's students (effectiveNurseIdsForRoom handles both).
export function nurseEditsForOverlay(
  overlay: NurseTransfers,
  allocs: { scheduleId: string; operatingRoomId?: string; allocatedNurseIds: string[] }[],
  isCrna: (id: string) => boolean,
): { scheduleId: string; nextNurseIds: string[] }[] {
  const edits: { scheduleId: string; nextNurseIds: string[] }[] = [];
  for (const a of allocs) {
    const rid = a.operatingRoomId;
    if (!rid || overlay[rid] === undefined) continue;  // only rooms the move actually touched
    edits.push({ scheduleId: a.scheduleId, nextNurseIds: effectiveNurseIdsForRoom(a.allocatedNurseIds, rid, overlay, isCrna) });
  }
  return edits;
}

// A board DROP is authoritative: the dropped CRNA takes this room and is released from every other one. The
// caller has already stripped them from the raw allocations of other rooms, so `rawCrnaByRoom` should reflect
// the POST-drop raw state; the destination key is cleared so the fresh raw allocation shows through.
export function dropNurseOnRoom(
  transfers: NurseTransfers,
  rawCrnaByRoom: RawCrnaByRoom,
  operatingRoomId: string,
  employeeId: string,
): NurseTransfers {
  const next = releaseNurse(transfers, rawCrnaByRoom, employeeId, operatingRoomId);
  delete next[operatingRoomId];
  return normalizeNurseTransfers(next, rawCrnaByRoom);
}
